<script>
import AdminLayout from '@layouts/admin-main'
import AccountCatalogNav from '@components/admin/account-catalog-nav'
import { fetchList, setAccountBalance } from '@apis/admin/account'
import trim from 'lodash/trim'
import LangInput from '@components/admin/lang-input'

export default {
  page: {
    title: 'Admin::AccountUpdateBalanceBulk',
    meta: [{ name: 'description', content: 'Admin::AccountUpdateBalanceBulk' }],
  },
  components: { AdminLayout, AccountCatalogNav, LangInput },
  data() {
    return {
      allowed_headers: ['user_name', 'account_number', 'is_address', 'amount'],
      bulk_rows: [],
      validate_account_loading: null,
      update_bulk_loading: null,
      params: {
        check_user_name: true,
        description: '',
        action: null,
      },
      is_destroying: false,
    }
  },
  beforeDestroy() {
    this.is_destroying = true
  },
  methods: {
    async handleFileSelect(e) {
      try {
        this.validate_account_loading = null

        const XLSX = await import('xlsx')

        const [file] = e.target.files
        const reader = new FileReader()
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const [sheet_name] = workbook.SheetNames

          this.setBulkRows(
            XLSX.utils.sheet_to_json(workbook.Sheets[sheet_name], {
              header: 1,
              raw: false,
            })
          )
        }
        reader.readAsArrayBuffer(file)
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    setBulkRows(sheet_data) {
      const headers = sheet_data.shift().map(trim)

      this.bulk_rows = sheet_data.map((val) => {
        const row = {
          account_seq: null,
          account_member_seq: null,
          account_user_name: null,
          result_message: '',
          status: 'PENDING',
          is_user_name_match: 'NA',
          is_account_exists: 'NA',
        }
        for (const allowed_header of this.allowed_headers) {
          row[allowed_header] = trim(val[headers.indexOf(allowed_header)])
        }

        row.amount = row.amount ? row.amount.replace(/,/g, '') : null
        return row
      })
    },
    async handleValidateAccount() {
      if (this.validate_account_loading) return

      this.validate_account_loading = true
      try {
        for (const row of this.bulk_rows) {
          if (this.is_destroying) break

          if (!row.account_number.length) {
            row.is_account_exists = 'N'
            row.is_user_name_match = 'N'

            continue
          }

          const { total_count, data } = await fetchList({
            search_target:
              row.is_address === 'Y' ? 'address' : 'account_number',
            search_keyword: row.account_number,
          })
          const account_info = data.pop()

          row.is_account_exists = total_count ? 'Y' : 'N'
          row.is_user_name_match =
            account_info && account_info.user_name === row.user_name ? 'Y' : 'N'
          row.account_seq = account_info ? account_info.seq : null
          row.account_member_seq = account_info ? account_info.member_seq : null
          row.account_user_name = account_info ? account_info.user_name : null
          row.asset_title = account_info ? account_info.asset_title : null
          row.asset_code = account_info ? account_info.asset_code : null
        }

        this.validate_account_loading = false
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    async handleDownload() {
      try {
        const XLSX = await import('xlsx')

        const workbook = XLSX.utils.table_to_book(this.$refs.result, {
          sheet: 'Result',
        })
        return XLSX.writeFile(workbook, 'account-update-balance-bulk.xlsx')
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    async handleSubmit() {
      if (this.update_bulk_loading !== null) return

      this.update_bulk_loading = true
      for (const row of this.bulk_rows) {
        if (this.is_destroying) break

        if (row.is_account_exists !== 'Y') {
          row.status = 'SKIPPED'
          if (row.is_address === 'Y')
            row.result_message = this.$t(
              'account_update_balance_bulk.msg_account_number'
            )
          else
            row.result_message = this.$t(
              'account_update_balance_bulk.msg_not_exist'
            )

          continue
        }

        if (this.params.check_user_name && row.is_user_name_match !== 'Y') {
          row.status = 'SKIPPED'
          row.result_message = this.$t(
            'account_update_balance_bulk.msg_not_match_depositor'
          )

          continue
        }

        try {
          const result = await setAccountBalance({
            account_seq: row.account_seq,
            amount: row.amount,
            ...this.params,
          })

          row.status = 'SUCCESS'
          row.result_message = result.message
        } catch (error) {
          row.status = 'ERROR'
          row.result_message = error.message
        }
      }

      this.update_bulk_loading = false
    },
  },
}
</script>

<template>
  <AdminLayout>
    <AccountCatalogNav></AccountCatalogNav>
    <template v-if="!bulk_rows.length">
      <h3 :class="$style.xeAdmin">{{
        $t('account_update_balance_bulk.choice')
      }}</h3>
      <form @submit.prevent="search">
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('account_update_balance_bulk.excel') }}</div></th
              >
              <td colspan="3"
                ><input
                  :disabled="
                    validate_account_loading !== null ||
                      update_bulk_loading !== null
                  "
                  type="file"
                  @change="handleFileSelect"
              /></td>
            </tr>
          </tbody>
        </table>
      </form>
    </template>
    <template v-if="bulk_rows.length && validate_account_loading === null">
      <h3 :class="$style.xeAdmin">{{
        $t('account_update_balance_bulk.prosecutor')
      }}</h3>
      <form @submit.prevent="handleSetBalance">
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="col">
                <div>
                  <a
                    href="#"
                    :class="[$style.button]"
                    @click.prevent="handleValidateAccount"
                    ><span>{{
                      $t('account_update_balance_bulk.run_account_validation')
                    }}</span></a
                  >
                </div>
              </th>
              <td colspan="3">
                <template v-if="validate_account_loading === null">
                  {{ $t('account_update_balance_bulk.msg_before_execution') }}
                </template>
                <template v-else-if="validate_account_loading === false">
                  {{
                    $t('account_update_balance_bulk.msg_execution_completed')
                  }}
                </template>
                <template v-else>
                  {{ $t('account_update_balance_bulk.msg_running') }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </form>
    </template>
    <template v-if="validate_account_loading === false">
      <h3 :class="$style.xeAdmin">{{
        $t('account_update_balance_bulk.balance_correction')
      }}</h3>
      <form @submit.prevent="handleSubmit">
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="col">
                <div>
                  {{ $t('account_update_balance_bulk.current_status') }}
                </div>
              </th>
              <td colspan="3">
                <template v-if="update_bulk_loading === null">
                  {{ $t('account_update_balance_bulk.msg_run_button') }}
                </template>
                <template v-else-if="update_bulk_loading === false">
                  {{ $t('account_update_balance_bulk.msg_download_excel') }}
                </template>
                <template v-else>
                  {{ $t('account_update_balance_bulk.msg_wait_a_moment') }}
                </template>
              </td>
            </tr>
            <tr>
              <th scope="col">
                <div>
                  {{ $t('account_update_balance_bulk.adjusted_amount') }}
                </div>
              </th>
              <td>
                <ElRadioGroup v-model="params.action" size="mini">
                  <ElRadioButton
                    v-for="val in [
                      ['PLUS', $t('account_update_balance_bulk.increase')],
                      ['MINUS', $t('account_update_balance_bulk.deductinga')],
                    ]"
                    :key="val[0]"
                    :label="val[0]"
                    >{{ val[1] }}</ElRadioButton
                  >
                </ElRadioGroup>
              </td>
              <th scope="col"
                ><div>{{ $t('account_update_balance_bulk.reason') }}</div></th
              >
              <td>
                <LangInput v-model="params.description" />
              </td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <label
                  ><input v-model="params.check_user_name" type="checkbox" />
                  {{ $t('account_update_balance_bulk.must_matcm') }}</label
                >
                {{ ' ' }}
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="$t('account_update_balance_bulk.practice')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </template>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleDownload"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <table
      ref="result"
      cellspacing="0"
      cellpadding="0"
      :class="$style.colTable"
    >
      <thead>
        <tr>
          <th scope="col"
            ><div>{{ $t('account_update_balance_bulk.number') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('account_update_balance_bulk.name') }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.bank_account_number')
            }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.deposit_only_addresses')
            }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('account_update_balance_bulk.amount') }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.account_number_existence')
            }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.depositor_name_consistency')
            }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.unique_number')
            }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.account_assets')
            }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.checked_depositor')
            }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.member_unique_number')
            }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('account_update_balance_bulk.state') }}</div></th
          >
          <th scope="col"
            ><div>{{
              $t('account_update_balance_bulk.result_message')
            }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in bulk_rows" :key="idx">
          <td :class="[$style.tCenter, $style.nowrap]">{{ idx + 1 }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ row.user_name }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            row.account_number
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ row.is_address }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ row.amount }}</td>
          <td
            :class="[
              $style[row.is_account_exists],
              $style.tCenter,
              $style.nowrap,
            ]"
            >{{ row.is_account_exists }}</td
          >
          <td
            :class="[
              $style[row.is_user_name_match],
              $style.tCenter,
              $style.nowrap,
            ]"
            >{{ row.is_user_name_match }}</td
          >
          <td :class="[$style.tCenter, $style.nowrap]">{{
            row.account_seq
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]"
            >{{ row.asset_title }} ({{ row.asset_code }})</td
          >
          <td :class="[$style.tCenter, $style.nowrap]">{{
            row.account_user_name
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            row.account_member_seq
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ row.status }}</td>
          <td :class="[$style.wide]">{{ row.result_message }}</td>
        </tr>
        <tr v-if="!bulk_rows.length">
          <td colspan="11" :class="[$style.tCenter]">{{
            $t('account_update_balance_bulk.msg_not_exist_excel')
          }}</td>
        </tr>
      </tbody>
    </table>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.rowTable th,
.rowTable td {
  vertical-align: middle;
}

table td.N {
  color: #fff;
  background-color: red;
}
</style>
